import { MenuService } from '@shared/services/menu.service';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { BaseAuthComponent } from '../../../../layouts/guest/pages/home/components/base-auth.component';
import { TokenRefresh } from '@shared/interfaces/auth/token-refresh.interface';
import { Login } from '@shared/interfaces/auth/login-data.interface';
import { FacebookLoginProvider, SocialUser } from '@abacritt/angularx-social-login';
import { SocialMediaUser } from '@shared/interfaces/auth/social-media-user.interface';
import { AuthComponent } from '@layouts/default/components/auth/auth.component';
import { Response } from '@shared/models/response.model';
import { User } from '@shared/models/user/user.model';
import { Error } from '@shared/interfaces/error.interface';
import { HeaderService } from '@shared/services/header.service';
import { CustomLogin } from '@shared/interfaces/custom-login.interface';
import { isMcdDashboardAccessOverrideEnabled } from '@shared/mcd-access';
import { logMcdDiagnostics } from '@shared/mcd-diagnostics';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent
  extends BaseAuthComponent
  implements OnInit, AfterViewInit, CustomLogin
{
  // Object was created to get provider without switch
  socialPlatforms: any = {
    FacebookLoginProvider,
  };
  dataParams: any;
  authFromHelpUkraine: boolean = false;
  authWcd: boolean;

  constructor(
    public authComponent: AuthComponent,
    public header: HeaderService,
    public navigationService: MenuService
  ) {
    super();
  }

  ngOnInit() {
    this.layoutTitle = this.translate.instant('GLOBAL.welcome');
    this.authComponent.titlePageEmitter.emit(this.layoutTitle);
    this.authFromHelpUkraine = this.navigationService.isUkraineUrl();
    this.authWcd = this.navigationService.isWcd();
  }

  ngAfterViewInit() {
    const subDataAuth = this.route.queryParamMap.subscribe((data: any) => {
      this.dataParams = data.params;
    });
    this.someSubscriptions.add(subDataAuth);
  }

  socialSignIn(socialPlatform: string) {
    this.socialMediaLoginWithoutGoogle(
      this.socialPlatforms[socialPlatform + 'LoginProvider'].PROVIDER_ID
    );
  }

  socialMediaLoginWithoutGoogle(provider: string) {
    if (provider.toLocaleLowerCase() !== 'google') {
      this.socialAuthService.signIn(provider).then((user: SocialUser) => {
        const pack: SocialMediaUser = {
          email: user.email,
          id: user.id,
          image: user.photoUrl,
          name: user.name,
          provider: user.provider.toLocaleLowerCase(),
          token: user.authToken,
        };
        const subLogin = this.authService.socialMediaAuth(pack).subscribe((token: TokenRefresh) => {
          this.storage.setToken(token);
          this.userAccess();
        }, this.onLoginError.bind(this));
        this.someSubscriptions.add(subLogin);
      });
    }
  }

  login() {
    this.loading = true;
    const pack: Login = this.form.value;
    if (this.storage.get('account_blocked')) {
      setTimeout(() => {
        this.loading = false;
        this.storage.delete('account_blocked');
      }, 1000);
    }
    const subLogin = this.authService.loginData(pack).subscribe((token: TokenRefresh) => {
      this.storage.setToken(token);
      this.userAccess();
    }, this.onLoginError.bind(this));
    this.someSubscriptions.add(subLogin);
  }

  onLoginError(error: Error) {
    switch (error?.message) {
      case 'account_blocked': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'wrong_token_passed': {
        this.toast.error(
          this.translate.instant('AUTH.password_will_not_be_changed'),
          this.translate.instant('AUTH.resetting_password_cancel')
        );
        break;
      }
      case 'user_exists': {
        this.toast.error(
          this.translate.instant('AUTH.user_exists'),
          this.translate.instant('AUTH.login_error')
        );
        this.socialAuthService.signOut();
        break;
      }
      case 'wrong_username_or_password': {
        this.toast.error(
          this.translate.instant('AUTH.wrong_username_or_password'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'account_is_blocked_by_administration': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked_by_administration'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      default: {
        this.toast.error(error.message, this.translate.instant('AUTH.login_error'));
      }
    }
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  userAccess() {
    const subUser = this.authService.userData().subscribe((resUser: Response<User>) => {
      const user = new User(resUser.data);
      const subGroups = this.storage.setUser(user).subscribe(() => {
        logMcdDiagnostics('login.userAccess', {
          source: 'AuthorizationService.userData',
          hasUser: !!user,
          path: typeof window !== 'undefined' ? window.location.pathname : null,
        });
        this.storage.setUserSettings({});
        this.languageService.updateCurrentLang(user.language ? user.language.code : undefined);
        this.authComponent.closeAuthModal();

        this.toast.success(
          this.translate.instant('AUTH.welcome') + user.username,
          this.translate.instant('AUTH.login_success')
        );
        this.loading = false;

        if (this.authFromHelpUkraine) {
          this.storage.setAuthFromUkraine(true);
        } else if (this.authWcd) {
          this.storage.setAuthFromWcd(true);
        }

        const isMcdEmployee = this.storage.isMcdEmployee;
        logMcdDiagnostics('login.redirectDecision', {
          hasUser: !!user,
          isMcdEmployee,
          overrideEnabled: isMcdDashboardAccessOverrideEnabled(),
          redirectToMcd: isMcdEmployee,
          path: typeof window !== 'undefined' ? window.location.pathname : null,
        });
        if (isMcdEmployee) {
          this.router.navigate(['/' + this.RoutesPath.default.mcd]);
        }
      });
      this.someSubscriptions.add(subGroups);
    }, this.onUserAccessError.bind(this));
    this.someSubscriptions.add(subUser);
  }

  onUserAccessError(error: Error) {
    this.loading = false;
    this.storage.logout();
    this.toast.error(
      this.translate.instant('AUTH.server_problem'),
      this.translate.instant('AUTH.login_error')
    );
  }
}
